import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  fetchNotificationConfig,
  fetchNotificationLog,
  sendNotificationTest,
  updateNotificationConfig,
} from "@/api/client";
import type {
  ChannelToggles,
  CredentialSource,
  NotificationChannelId,
  NotificationConfigPatch,
  NotificationConfigView,
  NotificationDeliveryStatus,
  NotificationEventType,
  NotificationLogEntry,
  NotificationUrgency,
} from "@/api/types";

const URGENCY_ROWS: Array<{ id: NotificationUrgency; label: string; hint: string; locked: boolean }> = [
  { id: "normal", label: "Normal", hint: "Scan summaries, daily report, routine notes", locked: false },
  { id: "urgent", label: "Urgent", hint: "Trades opened and closed, skipped entries", locked: false },
  { id: "critical", label: "Critical", hint: "Failures that need you. Always sent to every active channel", locked: true },
];

const EVENT_ROWS: Array<{ id: NotificationEventType; label: string }> = [
  { id: "position_opened", label: "Position opened" },
  { id: "position_partial_exit", label: "Partial exit" },
  { id: "position_closed_normal", label: "Position closed" },
  { id: "position_closed_emergency", label: "Emergency close" },
  { id: "entry_skipped", label: "Entry skipped" },
  { id: "kill_switch_triggered", label: "Kill switch" },
  { id: "technical_error", label: "Technical errors" },
  { id: "daily_pnl_report", label: "Daily report" },
  { id: "tuning_alert", label: "Tuning advice" },
  { id: "market_summary", label: "Scan summary" },
  { id: "profit_swept", label: "Profit swept" },
];

const CHANNELS: Array<{ id: NotificationChannelId; label: string }> = [
  { id: "discord", label: "Discord" },
  { id: "telegram", label: "Telegram" },
];

const SOURCE_LABEL: Record<CredentialSource, string> = {
  dashboard: "Saved in dashboard",
  deployment: "From deployment secret",
  mixed: "Partly from deployment secret",
  none: "Not configured",
};

const STATUS_STYLE: Record<NotificationDeliveryStatus, string> = {
  delivered: "border-profit/40 bg-profit/10 text-profit",
  partial: "border-gold/40 bg-gold/10 text-gold",
  pending: "border-paper/40 bg-paper/10 text-paper",
  failed: "border-sell/40 bg-sell/10 text-sell",
  skipped: "border-line bg-raised text-ink-faint",
};

function formatTime(seconds: number): string {
  return new Date(seconds * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 shrink-0 rounded-full border transition disabled:cursor-not-allowed disabled:opacity-40 ${
        checked ? "border-turquoise/60 bg-turquoise/30" : "border-line bg-raised"
      }`}
    >
      <span
        className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-all ${
          checked ? "left-[18px] bg-turquoise" : "left-0.5 bg-ink-faint"
        }`}
      />
    </button>
  );
}

function Check({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 cursor-pointer accent-turquoise disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
}

function Card({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-3 rounded-md border border-line-soft bg-raised/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="font-mono text-[11px] uppercase tracking-widest text-ink-dim">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  hint: string | null;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  type?: "text" | "password";
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between gap-2 font-mono text-[11px] text-ink-dim">
        <span>{label}</span>
        {hint && <span className="text-ink-faint">current {hint}</span>}
      </span>
      <input
        type={type}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-line bg-raised px-2 py-1.5 font-mono text-xs text-ink outline-none placeholder:text-ink-faint focus:border-turquoise/60"
      />
    </label>
  );
}

function Button({
  children,
  onClick,
  disabled,
  tone = "primary",
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "primary" | "ghost";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md border px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider transition disabled:cursor-not-allowed disabled:opacity-40 ${
        tone === "primary"
          ? "border-turquoise/50 bg-turquoise/10 text-turquoise hover:bg-turquoise/20"
          : "border-line text-ink-dim hover:border-turquoise/40 hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function SourceBadge({ source, active }: { source: CredentialSource; active: boolean }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[10px] text-ink-faint">
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-profit" : "bg-ink-faint"}`} />
      {active ? "Active" : "Inactive"} · {SOURCE_LABEL[source]}
    </span>
  );
}

interface Feedback {
  tone: "ok" | "bad";
  text: string;
}

function FeedbackLine({ feedback }: { feedback: Feedback | null }) {
  if (!feedback) return null;
  return (
    <div className={`break-words font-mono text-[11px] ${feedback.tone === "ok" ? "text-turquoise" : "text-sell"}`}>
      {feedback.text}
    </div>
  );
}

export default function NotificationSettings() {
  const [config, setConfig] = useState<NotificationConfigView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [discordFeedback, setDiscordFeedback] = useState<Feedback | null>(null);
  const [telegramFeedback, setTelegramFeedback] = useState<Feedback | null>(null);
  const [routingFeedback, setRoutingFeedback] = useState<Feedback | null>(null);
  const [log, setLog] = useState<NotificationLogEntry[]>([]);
  const [logError, setLogError] = useState<string | null>(null);

  const loadLog = useCallback(() => {
    fetchNotificationLog(30)
      .then((res) => {
        setLog(res.events);
        setLogError(null);
      })
      .catch((err) => setLogError(err instanceof Error ? err.message : "Could not load delivery log"));
  }, []);

  useEffect(() => {
    fetchNotificationConfig()
      .then((res) => setConfig(res.config))
      .catch((err) => setLoadError(err instanceof Error ? err.message : "Could not load notification settings"));
    loadLog();
  }, [loadLog]);

  async function save(
    key: string,
    patch: NotificationConfigPatch,
    setFeedback: (feedback: Feedback | null) => void,
    successText: string,
  ): Promise<boolean> {
    setBusy(key);
    setFeedback(null);
    try {
      const res = await updateNotificationConfig(patch);
      setConfig(res.config);
      setFeedback({ tone: "ok", text: successText });
      return true;
    } catch (err) {
      setFeedback({ tone: "bad", text: err instanceof Error ? err.message : "Save failed" });
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function test(
    channel: NotificationChannelId,
    body: { webhookUrl?: string; botToken?: string; chatId?: string },
    setFeedback: (feedback: Feedback | null) => void,
  ) {
    setBusy(`test-${channel}`);
    setFeedback(null);
    try {
      const res = await sendNotificationTest({ channel, ...body });
      setFeedback(
        res.ok
          ? { tone: "ok", text: "Test message delivered. Check the channel." }
          : { tone: "bad", text: res.error ?? "Delivery failed" },
      );
    } catch (err) {
      setFeedback({ tone: "bad", text: err instanceof Error ? err.message : "Test failed" });
    } finally {
      setBusy(null);
      loadLog();
    }
  }

  async function saveDiscord() {
    const patch: NotificationConfigPatch = { discord: {} };
    if (webhookUrl.trim() !== "") patch.discord = { webhookUrl: webhookUrl.trim() };
    const ok = await save("discord", patch, setDiscordFeedback, "Discord settings saved.");
    if (ok) setWebhookUrl("");
  }

  async function saveTelegram() {
    const telegram: NonNullable<NotificationConfigPatch["telegram"]> = {};
    if (botToken.trim() !== "") telegram.botToken = botToken.trim();
    if (chatId.trim() !== "") telegram.chatId = chatId.trim();
    const ok = await save("telegram", { telegram }, setTelegramFeedback, "Telegram settings saved.");
    if (ok) {
      setBotToken("");
      setChatId("");
    }
  }

  function channelToggle(channel: NotificationChannelId, value: boolean): Partial<ChannelToggles> {
    return channel === "discord" ? { discord: value } : { telegram: value };
  }

  function toggleChannel(channel: NotificationChannelId, enabled: boolean) {
    const patch: NotificationConfigPatch =
      channel === "discord" ? { discord: { enabled } } : { telegram: { enabled } };
    const setFeedback = channel === "discord" ? setDiscordFeedback : setTelegramFeedback;
    void save(`toggle-${channel}`, patch, setFeedback, enabled ? "Channel enabled." : "Channel disabled.");
  }

  function setUrgencyRoute(urgency: NotificationUrgency, channel: NotificationChannelId, value: boolean) {
    const urgencyRouting: NonNullable<NotificationConfigPatch["urgencyRouting"]> = {};
    urgencyRouting[urgency] = channelToggle(channel, value);
    void save(`urgency-${urgency}-${channel}`, { urgencyRouting }, setRoutingFeedback, "Routing saved.");
  }

  function setEventRoute(type: NotificationEventType, channel: NotificationChannelId, value: boolean) {
    const eventRouting: NonNullable<NotificationConfigPatch["eventRouting"]> = {};
    eventRouting[type] = channelToggle(channel, value);
    void save(`event-${type}-${channel}`, { eventRouting }, setRoutingFeedback, "Routing saved.");
  }

  async function clearCredential(channel: NotificationChannelId) {
    const patch: NotificationConfigPatch =
      channel === "discord" ? { discord: { webhookUrl: null } } : { telegram: { botToken: null, chatId: null } };
    const setFeedback = channel === "discord" ? setDiscordFeedback : setTelegramFeedback;
    await save(`clear-${channel}`, patch, setFeedback, "Dashboard credentials removed.");
  }

  if (loadError) {
    return (
      <div className="rounded-md border border-sell/40 bg-sell/10 px-3 py-2 font-mono text-xs text-sell">{loadError}</div>
    );
  }
  if (!config) {
    return <div className="font-mono text-xs text-ink-faint">Loading notification settings…</div>;
  }

  const discordTestBody = webhookUrl.trim() !== "" ? { webhookUrl: webhookUrl.trim() } : {};
  const telegramTestBody = {
    ...(botToken.trim() !== "" ? { botToken: botToken.trim() } : {}),
    ...(chatId.trim() !== "" ? { chatId: chatId.trim() } : {}),
  };

  return (
    <div className="space-y-6">
      <div className="font-mono text-[11px] leading-relaxed text-ink-faint">
        Credentials saved here take priority over deployment secrets. Every change is stored immediately and applies to the
        next alert. Stored secrets are never shown again, only their last four characters.
      </div>

      <Card
        title="Discord"
        right={
          <div className="flex items-center gap-3">
            <SourceBadge source={config.discord.source} active={config.discord.active} />
            <Switch
              checked={config.discord.enabled}
              disabled={busy !== null}
              label="Enable Discord"
              onChange={(next) => toggleChannel("discord", next)}
            />
          </div>
        }
      >
        <Field
          label="Webhook URL"
          hint={config.discord.webhookHint}
          value={webhookUrl}
          onChange={setWebhookUrl}
          type="password"
          placeholder={config.discord.configured ? "Leave empty to keep the current webhook" : "https://discord.com/api/webhooks/…"}
        />
        <label className="flex items-center gap-2 font-mono text-[11px] text-ink-dim">
          <Check
            checked={config.discord.mentionOnCritical}
            disabled={busy !== null}
            label="Mention everyone on critical alerts"
            onChange={(next) =>
              void save("discord-mention", { discord: { mentionOnCritical: next } }, setDiscordFeedback, "Saved.")
            }
          />
          Ping @here on critical alerts
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => void saveDiscord()} disabled={busy !== null || webhookUrl.trim() === ""}>
            {busy === "discord" ? "Saving…" : "Save webhook"}
          </Button>
          <Button
            tone="ghost"
            onClick={() => void test("discord", discordTestBody, setDiscordFeedback)}
            disabled={busy !== null || (!config.discord.configured && webhookUrl.trim() === "")}
          >
            {busy === "test-discord" ? "Sending…" : "Send test"}
          </Button>
          {config.discord.source === "dashboard" && (
            <Button tone="ghost" onClick={() => void clearCredential("discord")} disabled={busy !== null}>
              Remove saved webhook
            </Button>
          )}
        </div>
        <FeedbackLine feedback={discordFeedback} />
      </Card>

      <Card
        title="Telegram"
        right={
          <div className="flex items-center gap-3">
            <SourceBadge source={config.telegram.source} active={config.telegram.active} />
            <Switch
              checked={config.telegram.enabled}
              disabled={busy !== null}
              label="Enable Telegram"
              onChange={(next) => toggleChannel("telegram", next)}
            />
          </div>
        }
      >
        <Field
          label="Bot token"
          hint={config.telegram.botTokenHint}
          value={botToken}
          onChange={setBotToken}
          type="password"
          placeholder={config.telegram.botTokenHint ? "Leave empty to keep the current token" : "123456789:AA…"}
        />
        <Field
          label="Chat ID"
          hint={config.telegram.chatIdHint}
          value={chatId}
          onChange={setChatId}
          placeholder={config.telegram.chatIdHint ? "Leave empty to keep the current chat" : "-1001234567890 or @channelname"}
        />
        <label className="flex items-center gap-2 font-mono text-[11px] text-ink-dim">
          <Check
            checked={config.telegram.silentNormal}
            disabled={busy !== null}
            label="Silent delivery for normal alerts"
            onChange={(next) =>
              void save("telegram-silent", { telegram: { silentNormal: next } }, setTelegramFeedback, "Saved.")
            }
          />
          Deliver normal alerts silently (no sound)
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => void saveTelegram()}
            disabled={busy !== null || (botToken.trim() === "" && chatId.trim() === "")}
          >
            {busy === "telegram" ? "Saving…" : "Save Telegram"}
          </Button>
          <Button
            tone="ghost"
            onClick={() => void test("telegram", telegramTestBody, setTelegramFeedback)}
            disabled={busy !== null || (!config.telegram.configured && (botToken.trim() === "" || chatId.trim() === ""))}
          >
            {busy === "test-telegram" ? "Sending…" : "Send test"}
          </Button>
          {config.telegram.source === "dashboard" && (
            <Button tone="ghost" onClick={() => void clearCredential("telegram")} disabled={busy !== null}>
              Remove saved credentials
            </Button>
          )}
        </div>
        <FeedbackLine feedback={telegramFeedback} />
      </Card>

      <Card title="Routing by urgency">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[320px] font-mono text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-ink-faint">
                <th className="py-1 pr-2 font-medium">Urgency</th>
                {CHANNELS.map((channel) => (
                  <th key={channel.id} className="px-2 py-1 text-center font-medium">
                    {channel.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {URGENCY_ROWS.map((row) => (
                <tr key={row.id} className="border-t border-line-soft">
                  <td className="py-2 pr-2">
                    <div className="text-ink">{row.label}</div>
                    <div className="text-[10px] leading-snug text-ink-faint">{row.hint}</div>
                  </td>
                  {CHANNELS.map((channel) => (
                    <td key={channel.id} className="px-2 py-2 text-center">
                      <Check
                        label={`${row.label} to ${channel.label}`}
                        checked={row.locked ? config[channel.id].active : config.urgencyRouting[row.id][channel.id]}
                        disabled={row.locked || busy !== null}
                        onChange={(next) => setUrgencyRoute(row.id, channel.id, next)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Routing by event type">
        <div className="font-mono text-[10px] leading-snug text-ink-faint">
          A message is delivered to a channel only when both its urgency row and its event row allow that channel. Critical
          alerts ignore this table.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[320px] font-mono text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-ink-faint">
                <th className="py-1 pr-2 font-medium">Event</th>
                {CHANNELS.map((channel) => (
                  <th key={channel.id} className="px-2 py-1 text-center font-medium">
                    {channel.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {EVENT_ROWS.map((row) => {
                const toggles: ChannelToggles = config.eventRouting[row.id];
                return (
                  <tr key={row.id} className="border-t border-line-soft">
                    <td className="py-2 pr-2 text-ink">{row.label}</td>
                    {CHANNELS.map((channel) => (
                      <td key={channel.id} className="px-2 py-2 text-center">
                        <Check
                          label={`${row.label} to ${channel.label}`}
                          checked={toggles[channel.id]}
                          disabled={busy !== null}
                          onChange={(next) => setEventRoute(row.id, channel.id, next)}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <FeedbackLine feedback={routingFeedback} />
      </Card>

      <Card
        title="Recent deliveries"
        right={
          <Button tone="ghost" onClick={loadLog}>
            Refresh
          </Button>
        }
      >
        {logError && <div className="font-mono text-[11px] text-sell">{logError}</div>}
        {!logError && log.length === 0 && <div className="font-mono text-[11px] text-ink-faint">No alerts yet.</div>}
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {log.map((entry) => (
            <div key={entry.id} className="rounded-md border border-line-soft bg-panel/60 px-2.5 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 break-words font-mono text-xs text-ink">{entry.title}</div>
                <span
                  className={`shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase ${STATUS_STYLE[entry.status]}`}
                >
                  {entry.status}
                </span>
              </div>
              <div className="mt-0.5 font-mono text-[10px] text-ink-faint">
                {formatTime(entry.createdAt)} · {entry.urgency} · {entry.type.replace(/_/g, " ")}
                {entry.attempts > 1 ? ` · ${entry.attempts} attempts` : ""}
              </div>
              {entry.results
                .filter((result) => !result.success && result.error)
                .map((result) => (
                  <div key={result.channel} className="mt-1 break-words font-mono text-[10px] text-sell">
                    {result.channel}: {result.error}
                  </div>
                ))}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
